"use client";

import React, { useState } from "react";
import { Label, Input, ErrorMessage } from "@/components/ui/form-elements";
import { Button } from "@/components/ui/button";
import {
  createKeyResultAction,
  updateKeyResultAction,
} from "@/actions/goals-actions";

interface KeyResultsFormProps {
  goalId: string;
  initialData?: {
    id: string;
    title: string;
    targetValue: number;
    currentValue: number;
    unit: string;
  };
  onSuccess: () => void;
  onCancel: () => void;
}

export function KeyResultsForm({
  goalId,
  initialData,
  onSuccess,
  onCancel,
}: KeyResultsFormProps) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [targetValue, setTargetValue] = useState(
    initialData?.targetValue?.toString() || "",
  );
  const [currentValue, setCurrentValue] = useState(
    initialData?.currentValue?.toString() || "0",
  );
  const [unit, setUnit] = useState(initialData?.unit || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isEdit = !!initialData;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Il titolo è obbligatorio.");
      return;
    }
    const target = parseFloat(targetValue);
    const current = parseFloat(currentValue);

    if (isNaN(target) || target <= 0) {
      setError("Il valore target deve essere un numero maggiore di 0.");
      return;
    }
    if (isNaN(current) || current < 0) {
      setError(
        "Il valore corrente deve essere un numero valido maggiore o uguale a 0.",
      );
      return;
    }
    if (!unit.trim()) {
      setError("L'unità di misura (es. %, EUR, nodi) è obbligatoria.");
      return;
    }

    setLoading(true);
    try {
      let res;
      if (isEdit && initialData) {
        res = await updateKeyResultAction({
          id: initialData.id,
          goalId,
          title,
          currentValue: current,
          targetValue: target,
          unit,
        });
      } else {
        res = await createKeyResultAction({
          goalId,
          title,
          currentValue: current,
          targetValue: target,
          unit,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || "Qualcosa è andato storto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="kr-title">Titolo del Key Result</Label>
        <Input
          id="kr-title"
          placeholder="es. Raggiungere 50k visitatori mensili"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={loading}
          required
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="kr-target">Target</Label>
          <Input
            id="kr-target"
            type="number"
            step="any"
            placeholder="es. 100"
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            disabled={loading}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="kr-unit">Unità</Label>
          <Input
            id="kr-unit"
            placeholder="es. %, EUR, lead"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            disabled={loading}
            required
          />
        </div>
      </div>

      <div className="space-y-2 pt-1">
        <div className="flex justify-between items-center">
          <Label htmlFor="kr-current-range">Valore Corrente</Label>
          <span className="text-xs font-semibold text-primary">
            {targetValue && parseFloat(targetValue) > 0
              ? `${Math.round((parseFloat(currentValue || "0") / parseFloat(targetValue)) * 100)}%`
              : "0%"}
          </span>
        </div>
        <div className="flex items-center gap-4">
          <input
            id="kr-current-range"
            type="range"
            min="0"
            max={Math.max(
              parseFloat(targetValue || "0") || 100,
              parseFloat(currentValue || "0"),
            )}
            step={targetValue && parseFloat(targetValue) > 10 ? "1" : "any"}
            value={currentValue}
            onChange={(e) => setCurrentValue(e.target.value)}
            disabled={loading}
            className="flex-1 h-2 bg-neutral-200 dark:bg-neutral-800 rounded-lg appearance-none cursor-pointer accent-primary focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <div className="w-28">
            <Input
              id="kr-current"
              type="number"
              step="any"
              placeholder="es. 10"
              value={currentValue}
              onChange={(e) => setCurrentValue(e.target.value)}
              disabled={loading}
              required
              className="text-right"
            />
          </div>
        </div>
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Annulla
        </Button>
        <Button type="submit" loading={loading}>
          {isEdit ? "Salva Modifiche" : "Aggiungi Key Result"}
        </Button>
      </div>
    </form>
  );
}
